import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import googleAuthService from '../services/googleAuthService';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  useTheme,
  Divider
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import Toast from '../components/Toast';
import { useMetaPixel } from '../hooks/use-meta-pixel';
import { useGoogleAnalytics } from '../hooks/use-google-analytics';
import { useGoogleAds } from '../hooks/use-google-ads';

const Login = () => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, isAuthenticated, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [toast, setToast] = useState({ open: false, message: '', severity: 'error' });
  const { trackEvent } = useMetaPixel();
  const { trackLogin } = useGoogleAnalytics();
  const { trackLogin: trackAdsLogin } = useGoogleAds();

  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    const checkAuthentication = async () => {
      try {        
        // If not, check if there's a valid token that hasn't been processed yet
        const token = localStorage.getItem('token');

        // First check if we already know the user is authenticated
        if (isAuthenticated && token) {
          console.log('User already authenticated, redirecting to dashboard');
          navigate(from, { replace: true });
          return;
        }

        if (token) {
          console.log('Found existing token, validating...');
          setIsLoading(true);
          
          // Use the checkAuth function to verify the token
          const isValid = await checkAuth();
          
          if (isValid) {
            console.log('Existing token is valid, redirecting to dashboard');
            setToast({
              open: true,
              message: 'Already signed in! Redirecting...',
              severity: 'success'
            });
            
            setTimeout(() => {
              navigate(from, { replace: true });
            }, 1000);
          } else {
            // Token is invalid, we'll stay on the login page
            console.log('Existing token is invalid or expired');
            localStorage.removeItem('token');
          }
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        // Clear potentially invalid token
        localStorage.removeItem('token');
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuthentication();
  }, [isAuthenticated, checkAuth, navigate, from]);

  useEffect(() => {
    const initGoogleSignIn = async () => {
      try {
        setTimeout(async () => {
          const container = document.getElementById('google-signin-button');
          if (container) {
            try {
              console.log('Starting Google Sign-In process...');
              const googleResponse = await googleAuthService.handleGoogleSignIn(false);
              console.log('Google Sign-In response received:', googleResponse);

              if (googleResponse && googleResponse.token) {
                // Track successful Google login with Meta Pixel
                trackEvent('Lead', {
                  content_name: 'google_login',
                  content_category: 'authentication',
                  user_data: {
                    email: googleResponse.user?.email || ''
                  }
                });

                // Track successful Google login with Google Analytics
                trackLogin({
                  method: 'google',
                  user_data: {
                    email: googleResponse.user?.email || ''
                  }
                });

                // Track successful Google login with Google Ads
                trackAdsLogin({
                  method: 'google',
                  user_data: {
                    email: googleResponse.user?.email || ''
                  }
                });

                setToast({
                  open: true,
                  message: 'Google Sign-In successful! Redirecting...',
                  severity: 'success'
                });

                console.log('Authentication successful, token received:', !!googleResponse.token);

                setTimeout(() => {
                  const destination = from || '/dashboard';

                  if (destination === '/dashboard') {
                    console.log('Redirecting to dashboard with page refresh');
                    window.location.href = '/dashboard';
                  } else {
                    console.log('Navigating to:', destination);
                    navigate(destination, { replace: true });
                  }
                }, 1000);
              }
            } catch (authError) {
              console.error('Google Sign-In error details:', authError);

              let processedError = authError;
              if (typeof authError.message === 'string' && authError.message.startsWith('{')) {
                try {
                  processedError = JSON.parse(authError.message);
                } catch (e) {
                  console.log('Error parsing error message JSON:', e);
                }
              }

              // Check specifically for account not found / needs registration
              if (
                (processedError.code === 'ACCOUNT_NOT_FOUND' || authError.code === 'ACCOUNT_NOT_FOUND') || 
                (processedError.message && processedError.message.includes('account not found'))
              ) {
                console.log('Account not found error detected:', processedError || authError);
                
                // Extract user details
                const email = processedError.email || 
                              authError.email || 
                              (processedError.user && processedError.user.email) || 
                              '';
                              
                const name = processedError.name || 
                            authError.name || 
                            '';
                            
                const picture = processedError.picture || 
                               authError.picture || 
                               '';
                
                console.log('Extracted user details for registration:', { email, name, picture });

                // Show clear toast message about registration and ensure it's visible
                setToast({
                  open: true,
                  message: 'Your Google account is not registered yet. Redirecting to registration...',
                  severity: 'info'
                });

                // Clear any potentially invalid token
                localStorage.removeItem('token');
                
                // Use a longer delay to ensure toast is visible before redirect
                setTimeout(() => {
                  navigate('/register', { 
                    state: { 
                      email: email,
                      fromGoogle: true,
                      name: name,
                      picture: picture
                    }
                  });
                }, 2500); // Increased delay for better visibility
                
                return; // Important to prevent further processing
              } 

              // Keep the existing email verification handling
              if (processedError.code === 'EMAIL_NOT_VERIFIED' || authError.code === 'EMAIL_NOT_VERIFIED') {
                const userEmail = 
                  (processedError.user?.email) || 
                  (authError.user?.email) || 
                  '';
                  
                console.log('Email not verified, redirecting to verification page:', userEmail);

                setToast({
                  open: true,
                  message: 'Please verify your email address before accessing your account.',
                  severity: 'warning'
                });

                setTimeout(() => {
                  navigate('/verify-email', { 
                    state: { 
                      email: userEmail
                    }
                  });
                }, 1500);
                return;
              }

              // Generic error fallback
              setToast({
                open: true,
                message: processedError.message || authError.message || 'Google Sign-In failed. Please try again.',
                severity: 'error'
              });
            }
          } else {
            console.error('Google Sign-In button container not found in DOM');
          }
        }, 250);
      } catch (error) {
        console.error('Failed to initialize Google Sign-In:', error);
        setToast({
          open: true,
          message: 'Failed to initialize Google Sign-In. Please try again.',
          severity: 'error'
        });
      }
    };

    initGoogleSignIn();

    return () => {
      googleAuthService.cleanup();
    };
  }, [navigate, from, trackEvent, trackLogin, trackAdsLogin]);

  useEffect(() => {
    if (location.state?.message) {
      setError(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setError('');
    setIsLoading(true);
    
    try {
      if (!credentials.email || !credentials.password) {
        setToast({
          open: true,
          message: 'Email and password are required',
          severity: 'error'
        });
        setIsLoading(false);
        return;
      }
      
      const result = await login(credentials.email, credentials.password);
      
      console.log('Login result:', result);
      
      if (result.success && result.token) {
        // Track successful login with Meta Pixel
        trackEvent('Lead', {
          content_name: 'user_login',
          content_category: 'authentication',
          user_data: {
            email: credentials.email
          }
        });

        // Track successful login with Google Analytics
        trackLogin({
          method: 'email',
          user_data: {
            email: credentials.email
          }
        });

        // Track successful login with Google Ads
        trackAdsLogin({
          method: 'email',
          user_data: {
            email: credentials.email
          }
        });
        
        setToast({
          open: true,
          message: 'Login successful! Redirecting to dashboard...',
          severity: 'success'
        });

        setTimeout(() => {
          const destination = from || '/dashboard';
          if (destination === '/dashboard') {
            console.log('Redirecting to dashboard with page refresh');
            window.location.href = '/dashboard';
          } else {
            console.log('Navigating to:', destination);
            navigate(destination, { replace: true });
          }
        }, 1000);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Login error details:', err);
      setIsLoading(false);

      // Extract error details from various possible formats
      let errorCode = '';
      let errorMessage = '';
      
      // Handle structured API error response
      if (err.response?.data) {
        errorCode = err.response.data.code;
        errorMessage = err.response.data.message;
      } 
      // Handle JSON string error
      else if (typeof err.message === 'string' && err.message.startsWith('{')) {
        try {
          const parsedError = JSON.parse(err.message);
          errorCode = parsedError.code;
          errorMessage = parsedError.message;
        } catch (parseError) {
          console.log('Failed to parse error JSON:', parseError);
        }
      } 
      // Handle direct error object
      else if (err.code && err.message) {
        errorCode = err.code;
        errorMessage = err.message;
      }
      // Handle plain object with success property
      else if (typeof err === 'object' && err !== null && err.success === false) {
        errorCode = err.code || '';
        errorMessage = err.message || 'Authentication failed';
      }
      
      console.log('Processed error:', { code: errorCode, message: errorMessage });
      
      // Handle specific error cases based on error code
      switch (errorCode) {
        case 'ACCOUNT_NOT_FOUND':
          setToast({
            open: true,
            message: errorMessage || 'Account not found. Redirecting to registration...',
            severity: 'info'
          });

          if (err.redirectToRegister) {
            setTimeout(() => {
              navigate('/register', { 
                state: { email: credentials.email }
              });
            }, 1500);
          }
          break;
          
        case 'EMAIL_NOT_VERIFIED':
          setToast({
            open: true,
            message: errorMessage || 'Please verify your email address before logging in.',
            severity: 'warning'
          });

          const emailToUse = err.user?.email || credentials.email;

          setTimeout(() => {
            navigate('/verify-email', { 
              state: { email: emailToUse }
            });
          }, 1500);
          break;
          
        case 'INVALID_CREDENTIALS':
        case 'AUTH_REFRESH_FAILED':
        case 'AUTHENTICATION_FAILED':
          setToast({
            open: true,
            message: 'Incorrect email or password. Please try again.',
            severity: 'error'
          });
          break;
          
        case 'ACCOUNT_LOCKED':
        case 'ACCOUNT_DISABLED':
          setToast({
            open: true,
            message: errorMessage || 'Your account has been locked. Please contact support for assistance.',
            severity: 'error'
          });
          break;
          
        case 'TOO_MANY_REQUESTS':
          setToast({
            open: true,
            message: 'Too many login attempts. Please try again later.',
            severity: 'error'
          });
          break;
          
        default:
          // If we have an error message, use it
          if (errorMessage) {
            setToast({
              open: true,
              message: errorMessage,
              severity: 'error'
            });
          } else {
            // Generic fallback
            setToast({
              open: true,
              message: 'Login failed. Please check your credentials and try again.',
              severity: 'error'
            });
          }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title="Login | Gravity Point Media" 
        description="Sign in to your Gravity Point Media account to access your website support and maintenance services." 
      />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
          py: 4
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 6 },
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            <Box textAlign="center" mb={4}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(45deg, #1976d2, #2196f3)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent'
                }}
              >
                Welcome Back
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Sign in to your account to continue
              </Typography>
            </Box>

            {error && (
              <Alert 
                severity={error.includes('Redirecting') ? 'info' : 'error'}
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  '& .MuiAlert-icon': {
                    fontSize: '2rem'
                  }
                }}
              >
                {error}
                {error.includes('Redirecting') && (
                  <Box sx={{ mt: 1 }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography variant="caption" sx={{ ml: 1 }}>
                      Please wait...
                    </Typography>
                  </Box>
                )}
              </Alert>
            )}

            <Box sx={{ mb: 3 }}>
              <div 
                id="google-signin-button" 
                style={{ 
                  width: '100%', 
                  marginBottom: '16px',
                  display: 'flex',
                  justifyContent: 'center',
                  minHeight: '50px'
                }}
              ></div>
              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                  or
                </Typography>
              </Divider>
            </Box>

            <form 
              noValidate 
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <TextField
                fullWidth
                label="Email address"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={credentials.email}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && credentials.password) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={credentials.password}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowPassword(!showPassword);
                        }}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box mb={3} textAlign="right">
                <Link
                  to="/forgot-password"
                  style={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  Forgot password?
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(45deg, #1976d2, #2196f3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #1565c0, #1976d2)',
                  },
                  '&:disabled': {
                    background: 'linear-gradient(45deg, #90caf9, #bbdefb)',
                  }
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>

            <Box mt={4} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                Don't have an account?{' '}
                <Link
                  to="/register"
                  style={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  Create one now
                </Link>
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast({ ...toast, open: false })}
        autoHideDuration={toast.severity === 'info' ? 2500 : 6000} // Longer duration for info messages
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} // Make more visible
      />
    </>
  );
};

export default Login;